import { useTranslation } from 'react-i18next';

import { Alert } from '@/app/components/ui/Alert';
import { Card, CardHeader } from '@/app/components/ui/Card';
import { Skeleton } from '@/app/components/ui/Skeleton';
import { cn } from '@/lib/cn';
import { useAsync } from '@/lib/useAsync';
import type { DashboardPanelResult, DashboardPanelSpec } from '@/features/statistics/api';
import { runDashboardBatch } from '@/features/statistics/api';
import { PanelOutcome } from '@/features/statistics/StatsPanel';
import { panelTitle } from '@/features/statistics/panelTitle';

/**
 * Several panels, one batch call — so a page with four statistics elements
 * does one request instead of four. Pass `results` directly (already run,
 * e.g. from a saved dashboard or a preview) to skip the fetch and render
 * those instead.
 */
export function StatsGrid({
  panels,
  results,
  className,
}: {
  panels: DashboardPanelSpec[];
  results?: Record<string, DashboardPanelResult>;
  className?: string;
}) {
  const { t } = useTranslation();
  const shouldFetch = results === undefined;
  const batch = useAsync(
    () => (shouldFetch ? runDashboardBatch(panels) : Promise.resolve(null)),
    [shouldFetch, JSON.stringify(panels)],
  );

  if (panels.length === 0) return null;

  if (shouldFetch && batch.loading) {
    return (
      <div className={cn('grid gap-6 sm:grid-cols-2', className)}>
        {panels.map((panel) => (
          <Card key={panel.id}>
            <Skeleton kind="card" />
          </Card>
        ))}
      </div>
    );
  }

  if (shouldFetch && batch.error) {
    return (
      <Alert tone="danger">
        {t('statistics.status.loadFailed', { message: batch.error.message })}
      </Alert>
    );
  }

  const resolved = results ?? batch.data?.panels ?? {};

  return (
    <div className={cn('grid gap-6 sm:grid-cols-2', className)}>
      {panels.map((panel) => (
        <Card key={panel.id}>
          <CardHeader title={panelTitle(panel, t)} />
          <PanelOutcome spec={panel} result={resolved[panel.id]} />
        </Card>
      ))}
    </div>
  );
}
